import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import * as L from "leaflet";

type BaseUrls = { day: string; sat: string; hybrid: string };

const tileOpts: L.TileLayerOptions = {
  tileSize: 256,
  zoomOffset: 0,
  crossOrigin: true,
  updateWhenIdle: true,
  updateWhenZooming: false,
  keepBuffer: 4,
  noWrap: true,
  maxNativeZoom: 18,
};

export function InstallBaseLayers({
  urls,
  onReady,
}: {
  urls: BaseUrls;
  onReady?: (c: L.Control.Layers) => void;
}) {
  const map = useMap();

  const vectorRef = useRef<L.TileLayer | null>(null);
  const satRef = useRef<L.TileLayer | null>(null);
  const hybridRef = useRef<L.TileLayer | null>(null);
  const ctrlRef = useRef<L.Control.Layers | null>(null);

  // keep onReady stable for the effect
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  // guard against StrictMode double-invoke in dev
  const initedRef = useRef(false);

  // mount once: create layers + control, add default base
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;

    const vector = (vectorRef.current = L.tileLayer(urls.day, tileOpts).addTo(map));
    const sat = (satRef.current = L.tileLayer(urls.sat, tileOpts));
    const hybrid = (hybridRef.current = L.tileLayer(urls.hybrid, tileOpts));

    const ctrl = (ctrlRef.current = L.control.layers(
      { Vector: vector, Satellite: sat, Hybrid: hybrid },
      undefined,
      { collapsed: true, position: "bottomright" }
    ).addTo(map));

    const closeOnBaseChange = () => ctrl.collapse();
    map.on("baselayerchange", closeOnBaseChange);

    onReadyRef.current?.(ctrl);

    return () => {
      map.off("baselayerchange", closeOnBaseChange);
      if (ctrlRef.current) map.removeControl(ctrlRef.current);
      [vector, sat, hybrid].forEach((l) => map.hasLayer(l) && map.removeLayer(l));
      ctrlRef.current = null;
      vectorRef.current = satRef.current = hybridRef.current = null;
      initedRef.current = false;
    };
    // IMPORTANT: don't depend on urls or onReady (we update URLs below)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // update existing layers in place if URL strings change
  useEffect(() => { vectorRef.current?.setUrl(urls.day); },    [urls.day]);
  useEffect(() => { satRef.current?.setUrl(urls.sat); },       [urls.sat]);
  useEffect(() => { hybridRef.current?.setUrl(urls.hybrid); }, [urls.hybrid]);

  return null;
}
